/**
 * Maps denial-risk label strings to rounded header badge visuals.
 * @module denialRiskHeaderBadges
 */
import type { ReactNode } from "react";
import { JCodeIssueLabel } from "../../../../../enums/jCodeIssueLabel";
import { AlertTriangleIcon } from "../../../../../svg/alertTriangle";
import { RedFlagIcon } from "../../../../../svg/redFlag";

/** Shared styling for warning-style denial badges in the single-order header. */
const WARNING_BADGE_STYLE = {
  bgColor: "#FAE6E6",
  textColor: "#1F1F1F",
  borderColor: "#E88B8B",
} as const;

export interface DenialRiskRoundedBadgeConfig {
  id: string;
  text: string;
  icon: ReactNode;
  bgColor: string;
  textColor: string;
  borderColor: string;
}

/**
 * Builds rounded-badge props for a single denial-risk label from getAllOrderDenialRiskLabels.
 *
 * @param label - Human-readable denial label (JCodeIssueLabel values)
 * @returns Visual configuration for BadgeWithIconRounded
 */
export function getDenialRiskRoundedBadgeConfig(
  label: string,
): DenialRiskRoundedBadgeConfig {
  const base = { ...WARNING_BADGE_STYLE };
  if (label === JCodeIssueLabel.NON_PREFERRED_DRUGS) {
    return {
      id: "header-denial-non-preferred",
      text: label,
      icon: <AlertTriangleIcon width={12} height={12} />,
      ...base,
    };
  }
  if (
    label === JCodeIssueLabel.NCCN_GUIDELINES_NOT_MET ||
    label === JCodeIssueLabel.MISSING_DX_CODE
  ) {
    return {
      id: `header-denial-${label.replace(/\s+/g, "-").toLowerCase()}`,
      text: label,
      icon: <RedFlagIcon width={9.8} height={14} />,
      ...base,
    };
  }
  return {
    id: `header-denial-${label.replace(/\s+/g, "-").toLowerCase()}`,
    text: label,
    icon: <AlertTriangleIcon width={12} height={12} />,
    ...base,
  };
}
